import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { RootState } from '../../../redux/store';
import { usePageHeader } from '../../../context/PageHeaderContext';
import { scrollAreaSx } from '../../../utils/scrollSx';

interface PendingCommand {
  id: number;
  device_id: string;
  command: string;
  payload: Record<string, unknown> | null;
  status: string;
  created_at: string;
  sent_at: string | null;
  screen_id: number;
  screen_name: string;
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  pending:    { color: '#fbbf24', bg: 'rgba(251,191,36,.12)' },
  sent:       { color: '#60a5fa', bg: 'rgba(96,165,250,.12)' },
  done:       { color: '#34d399', bg: 'rgba(52,211,153,.12)' },
  error:      { color: '#f87171', bg: 'rgba(248,113,113,.12)' },
  expired:    { color: '#94a3b8', bg: 'rgba(148,163,184,.08)' },
};

function formatCmd(command: string): string {
  return command.replace(/_/g, ' ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const POLL_INTERVAL = 15_000;
const SENT_HIDE_DELAY = 60_000;

const CommandQueuePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => s.auth.value);
  const { setBreadcrumbs, setPublishConfig } = usePageHeader();
  const [commands, setCommands] = useState<PendingCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const hideTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    setBreadcrumbs([
      { label: t('nav.screens'), onClick: () => navigate('/screens') },
      { label: t('cmdQueue.title') },
    ]);
    setPublishConfig(null);
    return () => { setBreadcrumbs([]); };
  }, [setBreadcrumbs, setPublishConfig, navigate, t]);

  const fetchCommands = useCallback(async () => {
    try {
      const { data } = await axios.get<PendingCommand[]>('/screens/commands/pending');
      setCommands(data);
    } catch {
      // silent — auth interceptor handles 401
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + auto-poll every 15 s
  useEffect(() => {
    if (!auth) return;
    fetchCommands();
    const id = setInterval(fetchCommands, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [auth, fetchCommands]);

  // Clean up timers on unmount
  useEffect(() => {
    const timers = hideTimers.current;
    return () => { timers.forEach(clearTimeout); };
  }, []);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    try {
      await axios.post('/screens/publish-all');

      // Optimistically mark all current pending commands as sent
      const now = new Date().toISOString();
      setCommands(prev => prev.map(cmd =>
        cmd.status === 'pending' ? { ...cmd, status: 'sent', sent_at: now } : cmd,
      ));

      // Schedule removal of those commands after 1 minute
      setCommands(prev => {
        prev.forEach(cmd => {
          if (cmd.status === 'pending' && !hideTimers.current.has(cmd.id)) {
            const timer = setTimeout(() => {
              setCommands(c => c.filter(x => x.id !== cmd.id));
              hideTimers.current.delete(cmd.id);
            }, SENT_HIDE_DELAY);
            hideTimers.current.set(cmd.id, timer);
          }
        });
        return prev;
      });

      // Also fetch fresh list after a short delay to pick up newly created commands
      setTimeout(fetchCommands, 1500);
    } catch {
      // silent
    } finally {
      setPublishing(false);
    }
  }, [fetchCommands]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', ...scrollAreaSx }}>
    <Box sx={{ p: '16px', maxWidth: 860, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>
          {t('cmdQueue.title')}
        </Typography>

        {/* Refresh */}
        <Tooltip title={t('cmdQueue.refresh')} placement="bottom">
          <IconButton
            onClick={fetchCommands}
            size="small"
            sx={{
              width: 32, height: 32, border: '1px solid var(--glass-border)',
              bgcolor: 'var(--glass-bg)', color: 'var(--text-secondary)', borderRadius: '8px',
              '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent-light)', bgcolor: 'var(--glass-hover)' },
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </IconButton>
        </Tooltip>

        {/* Publish All */}
        <Box
          component="button"
          onClick={handlePublish}
          disabled={publishing}
          sx={{
            display: 'flex', alignItems: 'center', gap: '6px',
            px: '14px', py: '6px', borderRadius: '8px', border: 'none', cursor: publishing ? 'default' : 'pointer',
            fontSize: 13, fontWeight: 600,
            background: publishing
              ? 'var(--glass-hover)'
              : 'linear-gradient(135deg, var(--accent), var(--accent-light))',
            color: publishing ? 'var(--text-disabled)' : '#fff',
            boxShadow: publishing ? 'none' : '0 2px 8px var(--accent-glow)',
            transition: 'opacity .15s',
            opacity: publishing ? 0.7 : 1,
          }}
        >
          {!publishing && (
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 2 11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
          {publishing ? t('cmdQueue.publishing') : t('cmdQueue.publish')}
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: '12px', overflow: 'hidden' }}>
        {/* Table header */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 90px 120px',
          gap: 1, px: 2, py: 1.5,
          borderBottom: '1px solid var(--glass-border)',
          background: 'var(--glass-hover)',
        }}>
          {[t('cmdQueue.screen'), t('cmdQueue.command'), t('cmdQueue.status'), t('cmdQueue.createdAt')].map(h => (
            <Typography key={h} sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {h}
            </Typography>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{t('common.loading')}</Typography>
          </Box>
        ) : commands.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{t('cmdQueue.empty')}</Typography>
          </Box>
        ) : (
          commands.map((cmd, idx) => {
            const style = STATUS_COLORS[cmd.status] || { color: 'var(--text-disabled)', bg: 'transparent' };
            return (
              <Box
                key={cmd.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 90px 120px',
                  gap: 1, px: 2, py: 1.5,
                  borderBottom: idx < commands.length - 1 ? '1px solid var(--divider)' : 'none',
                  '&:hover': { background: 'var(--row-hover)' },
                  transition: 'background .12s',
                }}
              >
                {/* Screen */}
                <Box>
                  <Typography
                    onClick={() => navigate(`/screens/${cmd.screen_id}`)}
                    sx={{
                      fontSize: 13, fontWeight: 500, color: 'var(--accent-light)',
                      cursor: 'pointer', display: 'inline',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {cmd.screen_name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace', mt: '1px' }}>
                    {cmd.device_id}
                  </Typography>
                </Box>

                {/* Command */}
                <Box>
                  <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', textTransform: 'capitalize' }}>
                    {formatCmd(cmd.command)}
                  </Typography>
                  {cmd.payload && Object.keys(cmd.payload).length > 0 && (
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '1px' }}>
                      {JSON.stringify(cmd.payload)}
                    </Typography>
                  )}
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: '1px' }}>
                  <Box sx={{ px: '7px', py: '2px', borderRadius: '6px', bgcolor: style.bg, display: 'inline-flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: style.color }}>
                      {cmd.status}
                    </Typography>
                  </Box>
                </Box>

                {/* Created at */}
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono", monospace', pt: '3px' }}>
                  {formatDate(cmd.created_at)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>

      {commands.length > 0 && (
        <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mt: 1.5, textAlign: 'right' }}>
          {commands.length} {commands.length === 1 ? 'command' : 'commands'}
        </Typography>
      )}
    </Box>
    </Box>
  );
};

export default CommandQueuePage;
